import { useCallback } from "react";
import { Banknote } from "lucide-react";
import { useConfirm } from "@/components/ui/confirm-dialog";
import { useRestaurantCurrency } from "@/lib/hooks";
import { displayPrice, getOrderActionText } from "@/lib/utils";
import { hasApiErrorKey } from "@/lib/apiErrors";
import { Order, OrderStatus } from "@/redux/services/order/order.type";

const CLOSING_STATUSES: OrderStatus[] = ["canceled", "rejected"];

/** The API's 422 key when the order still holds collected money (SFAPI-114). */
export const COLLECTED_MONEY_VIOLATION =
  "validation.order.error.status.collected_payments_not_refunded";

/**
 * Money collected on the order and not yet refunded, in cents. Same basis as
 * the API's check in UpdateOrderStatusProcessor (paid − refunded).
 */
export const getUnrefundedAmount = (
  order: Pick<Order, "paidAmount" | "refundedAmount">,
): number => Math.max(0, (order.paidAmount ?? 0) - (order.refundedAmount ?? 0));

/** True when an RTK Query error is the API's collected-money refusal. */
export const isCollectedMoneyViolation = (error: unknown): boolean =>
  hasApiErrorKey(error, COLLECTED_MONEY_VIOLATION);

export interface CollectedMoneyBlockOptions {
  /**
   * Offer the refund straight from the dialog (order drawer). Without it the
   * dialog only explains where to refund.
   */
  onRefund?: (amount: number) => void;
  /** Where the user can refund, appended to the explanation. */
  refundHint?: string;
}

// The drawer has no standalone "Rembourser" button for a non-overpaid order:
// its own cancel/reject dialog is where the refund is offered (SFRESTO-180).
const defaultRefundHint = (status: OrderStatus): string =>
  `Ouvrez la commande, choisissez « ${getOrderActionText(status)} » puis « Rembourser ».`;

/**
 * An order that still holds collected money cannot be canceled or rejected
 * (SFAPI-114): the API refuses it until the money is refunded, and nothing is
 * refunded automatically. These helpers explain that to the user, in French,
 * and point to the refund action.
 *
 * - `blockIfCollected(order, status)` checks before calling the API: returns
 *   `true` (and shows the dialog) when the change must not be sent.
 * - `showBlocked(status, amount)` shows the same dialog after the API refused
 *   it (the local order was stale); `amount` may be unknown.
 */
export const useCollectedMoneyCancelBlock = () => {
  const confirm = useConfirm();
  const currency = useRestaurantCurrency();

  const showBlocked = useCallback(
    async (
      status: OrderStatus,
      amount: number | null,
      options: CollectedMoneyBlockOptions = {},
    ): Promise<void> => {
      const isReject = status === "rejected";
      const verb = isReject ? "refusez" : "annulez";
      const collected =
        amount != null && amount > 0
          ? `${displayPrice(amount, currency)} ont été encaissés sur cette commande et ne sont pas remboursés.`
          : "Cette commande a déjà été encaissée et n'est pas remboursée.";
      const canRefundHere = !!options.onRefund && amount != null && amount > 0;
      const hint = canRefundHere
        ? ""
        : ` ${options.refundHint ?? defaultRefundHint(status)}`;

      const ok = await confirm({
        title: isReject
          ? "Impossible de refuser cette commande"
          : "Impossible d'annuler cette commande",
        description: `${collected} Remboursez d'abord le client, puis ${verb} la commande.${hint}`,
        confirmText: canRefundHere
          ? `Rembourser ${displayPrice(amount ?? 0, currency)}`
          : "Compris",
        cancelText: "Fermer",
        hideCancel: !canRefundHere,
        icon: <Banknote className="h-6 w-6" />,
      });

      if (ok && canRefundHere) {
        options.onRefund?.(amount ?? 0);
      }
    },
    [confirm, currency],
  );

  const blockIfCollected = useCallback(
    async (
      order: Pick<Order, "paidAmount" | "refundedAmount">,
      status: OrderStatus,
      options: CollectedMoneyBlockOptions = {},
    ): Promise<boolean> => {
      if (!CLOSING_STATUSES.includes(status)) return false;
      const unrefunded = getUnrefundedAmount(order);
      if (unrefunded <= 0) return false;
      await showBlocked(status, unrefunded, options);
      return true;
    },
    [showBlocked],
  );

  return { blockIfCollected, showBlocked };
};
